<template>
  <v-container fluid class="talk-container pa-4 pa-sm-6 pa-md-8">
    <v-row justify="center">
      <v-col cols="12" sm="12" md="12" lg="12" xl="12">
        <v-card elevation="2" class="chat-card">
          <v-card-text class="chat-window" ref="messageContainer">
            <div class="messages">
              <div v-for="message in messages" :key="message.id" :class="{ 'my-message': message.isMine, 'other-message': !message.isMine }"
                class="message-wrapper">
                
                <!-- 显示引用链接 -->
                <template v-if="message.references && message.references.length > 0">
                  <div class="references">
                    参考资料：
                    <div v-for="(ref, index) in message.references" :key="index">
                      <!-- 判断是否以http开头，如果是以http开头，则显示成a标签，否则显示成span标签 -->
                      <span v-if="!ref.startsWith('http')">{{ ref }}</span>
                      <a v-else :href="ref" target="_blank">{{ ref }}</a>
                    </div>
                  </div>
                </template>
                
                <!-- 如果有推理部分，显示推理 -->
                <template v-if="message.reasoning">
                  <div class="reasoning-message">
                    <div v-html="renderMarkdown(message.reasoning)" class="markdown-content"></div>
                  </div>
                </template>
                
                <!-- 显示内容 -->
                <template v-if="message.text || message.progress">
                  <div v-if="message.isMarkdown" v-html="renderMarkdown(message.text)" class="markdown-content"></div>
                  <span v-else-if="message.text" style="white-space: pre-wrap;">{{ message.text }}</span>
                  
                  <!-- 进度提示放在气泡内部 -->
                  <div v-if="message.progress" class="progress-indicator">
                    <div class="progress-content">
                      <v-progress-circular
                        indeterminate
                        size="16"
                        width="2"
                        color="primary"
                        class="mr-2"
                      ></v-progress-circular>
                      {{ message.progress }}
                    </div>
                  </div>
                </template>
                
                <!-- 修改复制按钮，只在机器人消息中显示，且始终可见 -->
                <div v-if="!message.isMine" class="copy-btn-wrapper">
                  <v-btn
                    icon
                    x-small
                    class="copy-btn"
                    @click="copyMessage(message)"
                    title="复制内容"
                  >
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="chat-input">
            <v-file-input
              v-model="attachment"
              hide-input
              prepend-icon="mdi-paperclip"
              class="attachment-input"
              accept="image/*, .pdf, .doc, .docx, .xls, .xlsx, .txt"
              @change="handleFileChange"
            ></v-file-input>
            
            <!-- 附件上传成功后显示胶囊 -->
            <div v-if="attachmentUrls.length > 0" class="attachment-pills mr-2">
              <v-chip
                v-for="(url, index) in attachmentUrls"
                :key="index"
                close
                color="primary"
                text-color="white"
                class="mr-1 mb-1"
                @click:close="removeAttachment(index)"
              >
                <v-icon left small>{{ getAttachmentIcon(url) }}</v-icon>
                {{ getAttachmentName(url) }}
              </v-chip>
              <v-btn
                x-small
                icon
                color="error"
                class="ml-1"
                @click="clearAllAttachments"
                v-if="attachmentUrls.length > 1"
              >
                <v-icon small>mdi-delete-sweep</v-icon>
              </v-btn>
            </div>
            
            <v-textarea 
              v-model="newMessage" 
              label="输入问题，按回车键发送，Shift+回车换行" 
              filled 
              clearable 
              @keydown.enter="handleEnterKey"
              ref="messageInput" 
              dense 
              flat 
              hide-details 
              auto-grow
              rows="1"
              row-height="20"
              class="custom-text-field"
            ></v-textarea>
            <v-btn 
              icon 
              :class="[
                'send-button ml-2',
                newMessage.trim() || attachmentUrls.length > 0 ? 'active' : 'inactive'
              ]"
              @click="sendMessage"
              :disabled="isSending"
            >
              <v-icon>mdi-send</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- 添加 snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="2000"
      top
    >
      {{ snackbar.text }}
    </v-snackbar>

    <!-- 添加微型面板 -->
    <v-card class="floating-panel" style="position: absolute; top: 40%; right: 20px; transform: translateY(-50%);">
      <v-card-actions>
        <v-btn 
          icon 
          class="settings-button" 
          @click="showDialog = true" 
          title="设置参数"
        >
          <v-icon>mdi-cog</v-icon>
        </v-btn>
      </v-card-actions>
      <v-card-actions>
        <v-btn 
          icon 
          color="red" 
          class="clear-history-button" 
          @click="clearChatHistory" 
          title="清空聊天记录"
        >
          <v-icon>mdi-delete-sweep</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- 添加弹出面板 -->
    <v-dialog v-model="showDialog" max-width="500px" style="border-radius: 12px; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);">
      <v-card style="padding: 20px; border-radius: 12px;">
        <v-card-title class="text-h6" style="font-weight: bold;">设置</v-card-title>
        <v-card-text>
          <div>
            <v-menu
              v-model="authorMenu"
              :close-on-content-click="false"
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="selectedAuthorsTitles"
                  label="选择作者"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  dense
                ></v-text-field>
              </template>
              <v-card>
                <v-list height="300" class="overflow-y-auto">
                  <v-list-item
                    v-for="author in authors"
                    :key="author"
                    @click="toggleAuthor(author)"
                    :class="{ 'selected-author': selectedAuthors.includes(author) }"
                  >
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ author }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </div>
        </v-card-text>
        <v-card-actions style="justify-content: center;">
          <v-btn class="confirm-button" @click="submitSettings" style="background-color: #1e496e; margin: 0 auto; border-radius: 20px; transition: background-color 0.3s;" @mouseover="this.style.backgroundColor='#1a3d5a'" @mouseleave="this.style.backgroundColor='#1e496e'">确定</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { baseURL, AUTHORS } from '@/utils/config';
import service from '@/utils/request';
import { marked } from 'marked'; // 导入marked库
import DOMPurify from 'dompurify'; // 导入DOMPurify库用于防止XSS攻击

export default {
  name: "Talk",
  data() {
    return {
      newMessage: '',
      messages: [],
      isSending: false,
      showMenu: null,
      attachment: null,
      attachmentUrls: [],
      showClearDialog: false,
      messageBuffer: '', // 添加消息缓冲区
      snackbar: {
        show: false,
        text: '',
        color: 'success'
      },
      showDialog: false, // 控制弹出面板的显示
      selectedAuthors: [], // 存储选择的作者
      authors: AUTHORS, // 使用从 config.js 导入的作者列表
      showAuthorMenu: false, // 添加 showAuthorMenu 数据属性
      authorMenu: false,
      selectedAuthorsTitles: '',
    };
  },
  methods: {
    scrollToEnd() {
      let container = this.$refs.messageContainer;
      this.$nextTick(() => {
        container.scrollTop = container.scrollHeight;
      });
    },
    
    handleEnterKey(event) {
      // 如果按下Shift+Enter，则插入换行符而不发送消息
      if (event.shiftKey) {
        return;
      }
      
      // 如果只按下Enter，则阻止默认行为并发送消息
      event.preventDefault();
      this.sendMessage();
    },
    
    handleFileChange(file) {
      if (!file) {
        return;
      }
      
      // 显示上传中提示
      if (this.$toast && this.$toast.info) {
        this.$toast.info('附件上传中...');
      } else {
        console.log('附件上传中...');
      }
      
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('token', token);
      
      service.post('/api/wiki/talkAttachment', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }).then(response => {
        // 添加到附件URL数组中
        this.attachmentUrls.push(response.data);
        if (this.$toast && this.$toast.success) {
          this.$toast.success('附件上传成功');
        } else {
          console.log('附件上传成功');
        }
      }).catch(error => {
        console.error('附件上传失败:', error);
        if (this.$toast && this.$toast.error) {
          this.$toast.error('附件上传失败: ' + ((error.response && error.response.data) || error.message));
        } else {
          console.error('附件上传失败: ' + ((error.response && error.response.data) || error.message));
        }
      }).finally(() => {
        // 清空文件输入框，以便可以再次选择同一文件
        this.attachment = null;
      });
    },
    
    // 移除指定索引的附件
    removeAttachment(index) {
      this.attachmentUrls.splice(index, 1);
    },
    
    // 清空所有附件
    clearAllAttachments() {
      this.attachmentUrls = [];
    },
    
    // 获取附件名称
    getAttachmentName(url) {
      if (!url) return '';
      
      // 从URL中提取文件名
      const parts = url.split('/');
      let fileName = parts[parts.length - 1];
      
      // 如果文件名太长，截断显示
      if (fileName.length > 15) {
        fileName = fileName.substring(0, 12) + '...';
      }
      
      return fileName;
    },
    
    // 根据附件类型获取对应的图标
    getAttachmentIcon(url) {
      if (!url) return 'mdi-file-outline';
      
      if (this.isImageAttachment(url)) {
        return 'mdi-file-image-outline';
      }
      
      if (url.endsWith('.pdf')) {
        return 'mdi-file-pdf-outline';
      }
      
      if (url.endsWith('.doc') || url.endsWith('.docx')) {
        return 'mdi-file-word-outline';
      }
      
      if (url.endsWith('.xls') || url.endsWith('.xlsx')) {
        return 'mdi-file-excel-outline';
      }
      
      if (url.endsWith('.txt')) {
        return 'mdi-file-document-outline';
      }
      
      return 'mdi-file-outline';
    },
    
    hasAttachment(text) {
      if (!text) return false;
      
      // 检查消息中是否包含附件URL
      const urls = this.getAllAttachmentUrls(text);
      return urls.length > 0;
    },
    
    getAttachmentUrl(text) {
      if (!text) return null;
      
      const urls = this.getAllAttachmentUrls(text);
      return urls.length > 0 ? urls[0] : null;
    },
    
    // 获取消息中的所有附件URL
    getAllAttachmentUrls(text) {
      if (!text) return [];
      
      const urls = [];
      // 使用正则表达式匹配URL
      const regex = /(https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp|pdf|doc|docx|xls|xlsx|txt))/gi;
      let match;
      
      while ((match = regex.exec(text)) !== null) {
        urls.push(match[1]);
      }
      
      return urls;
    },
    
    isImageAttachment(url) {
      if (!url) return false;
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
    },
    
    openAttachment(url) {
      if (!url) return;
      window.open(url, '_blank');
    },
    
    submitSettings() {
      this.showDialog = false; // 关闭面板
    },
    
    sendMessage() {
      if (this.isSending) {
        return;
      }
      
      if (!this.newMessage.trim() && this.attachmentUrls.length === 0) {
        return;
      }
      
      this.isSending = true;
      let messageContent = this.newMessage;
      
      // 添加用户提问的消息
      const newMessageId = this.messages.length + 1;
      this.addMessage(messageContent, true, newMessageId);
      
      // 添加一个空的机器人回答消息，并立即显示"正在准备回答"的进度
      this.addMessage("", false, newMessageId + 1, '', [], "正在准备回答");
      this.scrollToEnd();

      const token = localStorage.getItem('token');
      
      // 构建查询参数
      let queryParams = new URLSearchParams({
        question: messageContent,
        token: token
      });

      // 添加作者信息
      if (this.selectedAuthors.length > 0) {
        this.selectedAuthors.forEach(author => {
          queryParams.append('author', author);
        });
      }

      // 添加附件URL
      if (this.attachmentUrls.length > 0) {
        this.attachmentUrls.forEach(url => {
          queryParams.append('attachmentUrls', url);
        });
      }

      // 创建 EventSource 连接
      const eventSource = new EventSource(
        `${baseURL}/agent-api/talk?${queryParams.toString()}`
      );

      const answerMessageIndex = this.messages.length - 1;

      // 处理进度事件
      eventSource.addEventListener('progress', (event) => {
        const data = JSON.parse(event.data);
        this.messages[answerMessageIndex].progress = data.content;
      });

      // 处理推理事件
      eventSource.addEventListener('reasoning', (event) => {
        const data = JSON.parse(event.data);
        // 收到推理信息时，清除进度提示
        this.messages[answerMessageIndex].progress = null;
        if (!this.messages[answerMessageIndex].reasoning) {
          this.messages[answerMessageIndex].reasoning = data.content;
        } else {
          this.messages[answerMessageIndex].reasoning += data.content;
        }
        this.scrollToEnd();
      });

      // 处理内容事件
      eventSource.addEventListener('content', (event) => {
        const data = JSON.parse(event.data);
        // 收到内容时清除进度信息
        this.messages[answerMessageIndex].progress = null;
        if (!this.messages[answerMessageIndex].text) {
          this.messages[answerMessageIndex].text = data.content;
        } else {
          this.messages[answerMessageIndex].text += data.content;
        }
        this.messages[answerMessageIndex].isMarkdown = true;
        this.scrollToEnd();
      });

      // 处理引用事件
      eventSource.addEventListener('reference', (event) => {
        const data = JSON.parse(event.data);
        if (!this.messages[answerMessageIndex].references) {
          this.messages[answerMessageIndex].references = [];
        }
        this.messages[answerMessageIndex].references.push(data.content);
        this.scrollToEnd();
      });

      // 处理错误
      eventSource.onerror = (error) => {
        // 检查连接状态
        if (eventSource.readyState === EventSource.CLOSED) {
          // 连接已经关闭，这是正常的
          console.log('SSE连接已正常关闭');
          // 如果已经有内容，就不显示错误信息
          if (this.messages[answerMessageIndex] && 
              (this.messages[answerMessageIndex].text || 
               this.messages[answerMessageIndex].reasoning)) {
            // 有内容就不做任何处理
            return;
          }
        } else {
          // 这是真正的错误
          console.error('SSE错误:', error);
        }

        // 移除进度提示
        if (this.messages[answerMessageIndex]) {
          this.messages[answerMessageIndex].progress = null;
        }

        // 关闭连接并重置状态
        eventSource.close();
        this.isSending = false;
        this.scrollToEnd();
      };

      // 处理完成事件
      eventSource.addEventListener('complete', () => {
        // 移除进度提示
        if (this.messages[answerMessageIndex]) {
          this.messages[answerMessageIndex].progress = null;
        }
        eventSource.close();
        this.isSending = false;
        this.scrollToEnd();
      });

      // 添加超时处理
      setTimeout(() => {
        if (eventSource.readyState !== EventSource.CLOSED) {
          console.log('SSE超时关闭');
          // 移除进度提示
          if (this.messages[answerMessageIndex]) {
            this.messages[answerMessageIndex].progress = null;
          }
          eventSource.close();
          this.isSending = false;
          this.scrollToEnd();
        }
      }, 5 * 60 * 1000); // 5分钟超时

      // 重置消息和附件
      this.newMessage = '';
      this.attachment = null;
      this.attachmentUrls = [];
    },

    addMessage(text, isMine, id, reasoning = '', references = [], progress = null) {
      this.messages.push({
        id: id,
        text: text,
        isMine: isMine,
        reasoning: reasoning,
        references: references,
        isMarkdown: !isMine,
        progress: progress
      });
      this.scrollToEnd();
    },

    loadChatHistory() {
      const token = localStorage.getItem('token');
      service.get(`/api/wiki/talklog?token=${token}`).then(response => {
        this.messages = response.data.map((msg, index) => ({
          id: msg.id,
          text: msg.content,
          references: msg.references || [],
          isMine: msg.role === 'question',
          isMarkdown: msg.role !== 'question', // 假设非用户消息是Markdown格式
        }));
        this.scrollToEnd();
      });
    },
    
    // 清空聊天记录
    clearChatHistory() {
      const token = localStorage.getItem('token');
      service.post(`/api/wiki/clearTalkHistory?token=${token}`)
        .then(response => {
          this.messages = [];
          if (this.$toast && this.$toast.success) {
            this.$toast.success('聊天记录已清空');
          } else {
            console.log('聊天记录已清空');
          }
        })
        .catch(error => {
          console.error('清空聊天记录失败:', error);
          if (this.$toast && this.$toast.error) {
            this.$toast.error('清空聊天记录失败: ' + ((error.response && error.response.data) || error.message));
          } else {
            console.error('清空聊天记录失败: ' + ((error.response && error.response.data) || error.message));
          }
        });
    },

    copyMessage(message) {
      let content = [];
      
      // 如果有推理部分，添加推理内容和分隔线
      if (message.reasoning) {
        content.push(
          message.reasoning,
          '-------------------' // 添加分隔线
        );
      }
      
      // 添加主要文本内容
      if (message.text) {
        content.push(message.text);
      }
      
      // 如果有引用资料，添加引用
      if (message.references && message.references.length > 0) {
        content.push('参考资料：\n' + message.references.join('\n'));
      }
      
      // 使用数组join来连接内容，这样可以避免多余的换行
      const finalContent = content.filter(Boolean).join('\n\n').trim();
      
      // 使用 navigator.clipboard API 复制内容
      navigator.clipboard.writeText(finalContent).then(() => {
        this.snackbar = {
          show: true,
          text: '内容已复制到剪贴板',
          color: 'success'
        };
      }).catch(err => {
        console.error('复制失败:', err);
        this.snackbar = {
          show: true,
          text: '复制失败',
          color: 'error'
        };
      });
    },

    toggleAuthor(author) {
      const index = this.selectedAuthors.indexOf(author);
      if (index === -1) {
        this.selectedAuthors.push(author);  // 添加选中的作者
      } else {
        this.selectedAuthors.splice(index, 1);  // 移除取消选中的作者
      }
      this.selectedAuthorsTitles = this.selectedAuthors.join(', ');  // 更新显示的作者标题
    },

    // 添加Markdown渲染方法
    renderMarkdown(text) {
      if (!text) return '';
      // 使用marked解析markdown，并用DOMPurify清理HTML以防止XSS攻击
      return DOMPurify.sanitize(marked(text));
    },
  },
  mounted() {
    this.$refs.messageInput.focus();
    this.loadChatHistory();
  },
  created() {
  },
};
</script>

<style scoped>
.talk-container {
  background-color: #f8f9fa;
  min-height: calc(100vh - 100px); 
  padding: 0 !important;
  width: 100%;
  max-width: 100%;
}

.chat-card {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 100px - 16px);
  border-radius: 8px; /* 添加适当的圆角 */
  margin: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08); /* 增强阴影效果 */
  overflow: hidden; /* 确保内容不超出圆角 */
}

.chat-window {
  flex-grow: 1;
  overflow-y: auto;
  padding: 20px;
  background-color: #f8f9fa; /* 轻微的背景色 */
  background-image: linear-gradient(rgba(255, 255, 255, 0.7) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(255, 255, 255, 0.7) 1px, transparent 1px);
  background-size: 20px 20px; /* 网格大小 */
  background-position: center center;
}

.messages {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.my-message,
.other-message {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 18px;
  margin-bottom: 20px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  line-height: 1.5;
  position: relative;
  transition: transform 0.2s ease;
}

.my-message {
  background-color: #e3f2fd; /* 更柔和的蓝色 */
  align-self: flex-end;
  border-bottom-right-radius: 4px; /* 尖角效果 */
}

.my-message:hover {
  transform: translateX(-2px);
}

.other-message {
  background-color: #FFFFFF;
  align-self: flex-start;
  border-bottom-left-radius: 4px; /* 尖角效果 */
}

.other-message:hover {
  transform: translateX(2px);
}

.chat-input {
  padding: 12px 16px;
  display: flex;
  align-items: center;
  width: 100%;
  background-color: #fff;
  border-top: 1px solid #eaeaea;
}

.attachment-input {
  max-width: 40px;
  margin-right: 10px;
}

.attachment-pills {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  max-width: 100%;
}

.custom-text-field {
  flex-grow: 1;
  max-height: 150px;
  overflow-y: auto;
  border-radius: 24px;
  margin: 0 8px;
}

.custom-text-field >>> .v-input__slot {
  background-color: #f5f5f5 !important;
  min-height: 44px !important;
  overflow-y: hidden !important;
  border-radius: 24px !important;
  padding: 0 16px !important;
  transition: background-color 0.3s;
}

.custom-text-field:hover >>> .v-input__slot {
  background-color: #eeeeee !important;
}

.custom-text-field >>> textarea {
  overflow-y: hidden !important;
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}

.custom-text-field >>> .v-text-field__details {
  display: none;
}

.send-button {
  min-width: 44px;
  height: 44px;
  border-radius: 50%;
  transition: all 0.3s ease !important;
}

/* 无内容状态 */
.send-button.inactive {
  background-color: #f5f5f5 !important;
}

.send-button.inactive .v-icon {
  color: #999 !important;
}

/* 有内容状态 */
.send-button.active {
  background-color: #e3f2fd !important;
}

.send-button.active .v-icon {
  color: #1976d2 !important;
}

/* 悬浮效果 */
.send-button.active:hover {
  background-color: #1976d2 !important;
}

.send-button.active:hover .v-icon {
  color: white !important;
}

.send-button.inactive:hover {
  background-color: #eeeeee !important;
}

/* 按下效果 */
.send-button:not(:disabled):active {
  transform: scale(0.95);
}

/* 推理部分样式 */
.reasoning-message {
  background-color: #fff8e1;
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 12px;
  border-left: 3px solid #ffc107;
}

.reasoning-message .markdown-content {
  margin: 0; /* 移除默认的markdown内容边距 */
}

/* 确保推理部分的markdown内容继承字体粗细 */
.reasoning-message .markdown-content :deep(p) {
  margin-bottom: 4px;
  font-weight: inherit;
}

/* 推理部分最后一个段落不需要底部边距 */
.reasoning-message .markdown-content :deep(p:last-child) {
  margin-bottom: 0;
}

/* 引用部分样式 */
.references {
  margin-top: 12px;
  color: #0066cc;
  background-color: rgba(0, 102, 204, 0.05);
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.8em;
}

.references a {
  color: #0066cc;
  text-decoration: none;
  border-bottom: 1px dashed #0066cc;
  transition: color 0.2s, border-bottom 0.2s;
}

.references a:hover {
  color: #004c99;
  border-bottom: 1px solid #004c99;
}

/* 附件显示样式 */
.attachment-preview {
  margin-top: 12px;
  display: flex;
  align-items: center;
}

.attachment-preview img {
  max-width: 200px;
  max-height: 150px;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
}

.attachment-preview img:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.attachment-link {
  color: #1976d2;
  text-decoration: underline;
  word-break: break-all;
}

/* 响应式调整 */
@media (max-width: 600px) {
  .my-message,
  .other-message {
    max-width: 85%;
  }
  
  .chat-window {
    padding: 12px;
  }
  
  .chat-input {
    padding: 8px 12px;
  }
}

/* 添加滚动条样式 */
.chat-window::-webkit-scrollbar {
  width: 6px;
}

.chat-window::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.chat-window::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.chat-window::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 添加消息时间戳样式 */
.my-message::after,
.other-message::after {
  content: '';
  position: absolute;
  bottom: -12px;
  font-size: 0.7em;
  color: #888;
  white-space: nowrap;
}

.my-message::after {
  right: 8px;
}

.other-message::after {
  left: 8px;
}

/* 添加Markdown内容样式 */
.markdown-content {
  white-space: normal;
}

.markdown-content :deep(h1),
.markdown-content :deep(h2),
.markdown-content :deep(h3),
.markdown-content :deep(h4),
.markdown-content :deep(h5),
.markdown-content :deep(h6) {
  margin-top: 16px;
  margin-bottom: 8px;
  font-weight: 600;
}

.markdown-content :deep(h1) {
  font-size: 1.5em;
}

.markdown-content :deep(h2) {
  font-size: 1.3em;
}

.markdown-content :deep(h3) {
  font-size: 1.2em;
}

.markdown-content :deep(p) {
  margin-bottom: 8px;
}

.markdown-content :deep(ul),
.markdown-content :deep(ol) {
  padding-left: 20px;
  margin-bottom: 8px;
}

.markdown-content :deep(li) {
  margin-bottom: 4px;
}

.markdown-content :deep(code) {
  background-color: rgba(0, 0, 0, 0.05);
  padding: 2px 4px;
  border-radius: 3px;
  font-family: monospace;
}

.markdown-content :deep(pre) {
  background-color: rgba(0, 0, 0, 0.05);
  padding: 12px;
  border-radius: 6px;
  overflow-x: auto;
  margin-bottom: 12px;
}

.markdown-content :deep(pre code) {
  background-color: transparent;
  padding: 0;
}

.markdown-content :deep(blockquote) {
  border-left: 4px solid #ddd;
  padding-left: 12px;
  color: #666;
  margin-left: 0;
  margin-right: 0;
}

.markdown-content :deep(a) {
  color: #1976d2;
  text-decoration: none;
}

.markdown-content :deep(a:hover) {
  text-decoration: underline;
}

.markdown-content :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin-bottom: 12px;
}

.markdown-content :deep(th),
.markdown-content :deep(td) {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

.markdown-content :deep(th) {
  background-color: #f2f2f2;
}

.markdown-content :deep(img) {
  max-width: 100%;
  border-radius: 4px;
}

/* 修改进度提示样式 */
.progress-indicator {
  margin: 8px 0;
}

.progress-content {
  display: inline-flex;
  align-items: center;
  background-color: rgba(25, 118, 210, 0.1);
  color: #1976d2;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 0.9em;
  white-space: nowrap;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 移除之前的margin-top设置，因为进度提示现在在气泡内部 */
.my-message,
.other-message {
  position: relative;
  margin-bottom: 20px;
}

/* 第一条消息不需要顶部边距的规则也可以移除了 */
.messages > div:first-child {
  margin-top: 0;
}

.message-wrapper {
  position: relative;
}

.copy-btn {
  opacity: 0.6;
  transition: all 0.2s ease;
}

.copy-btn:hover {
  opacity: 1;
  transform: scale(1.1);
}

.copy-btn-wrapper {
  margin-top: 8px;
  padding-left: 4px;
  display: flex;
  align-items: center;
}

/* 移除之前的右侧padding设置 */
.my-message,
.other-message {
  padding-right: 16px; /* 恢复正常的padding */
}

.settings-button {
  position: relative;
}

.clear-history-button {
  position: relative;
}

.settings-button:hover,
.clear-history-button:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.selected-author {
  background-color: #e3f2fd; /* 选中时的背景色 */
  font-weight: bold; /* 选中时加粗 */
}

.floating-panel {
  position: absolute; /* 设置为绝对定位 */
  top: 40%; /* 向上移动 */
  right: 20px; /* 右侧距离 */
  transform: translateY(-50%); /* 垂直居中 */
  padding: 2px; /* 进一步减小内边距 */
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2); /* 添加阴影效果 */
  border-radius: 8px; /* 添加圆角 */
  z-index: 1000; /* 确保面板在最上层 */
  display: flex; /* 使用flex布局 */
  flex-direction: column; /* 垂直排列按钮 */
  align-items: center; /* 中心对齐 */
}

/* 确保设置按钮和清空聊天记录按钮的样式一致 */
.settings-button,
.clear-history-button {
  margin: 2px 0; /* 进一步减小按钮之间的间距 */
  width: 30px; /* 设置按钮宽度 */
  height: 30px; /* 设置按钮高度 */
  display: flex; /* 使用flex布局 */
  justify-content: center; /* 水平居中 */
  align-items: center; /* 垂直居中 */
  background-color: #fff; /* 背景色 */
  border: none; /* 去掉边框 */
  border-radius: 50%; /* 圆形按钮 */
  transition: background-color 0.3s; /* 添加过渡效果 */
}

.settings-button:hover,
.clear-history-button:hover {
  background-color: rgba(0, 0, 0, 0.1); /* 悬停时的背景色 */
}

.v-card-actions {
  display: flex; /* 确保使用flex布局 */
  justify-content: center; /* 水平居中 */
  align-items: center; /* 垂直居中 */
  padding: 0; /* 去掉内边距 */
}

.confirm-button {
  background-color: #4CAF50 !important; /* 设置按钮颜色为绿色 */
  color: white !important; /* 设置文本颜色为白色 */
  transition: background-color 0.3s; /* 添加过渡效果 */
}

.confirm-button:hover {
  background-color: #45a049 !important; /* 悬停时的颜色 */
}
</style>